// @ts-strict-ignore
import React from 'react';
import { SectionList } from 'react-native';
import { MBUserCollage, MBUserImage } from '@shared/components/image';
import { MBChatInputMentionRowWithOnPress } from '../rowWithOnPress';
import { Styles } from '../styles';

const keyExtractor = (item: MBAPI.ShortUser | MBAPI.ChannelGroup): string => String(item.id);

export interface MBChatInputMentionListProps {
  users: MBAPI.ShortUser[];
  groups: MBAPI.ChannelGroup[];
  onPressUser(user: MBAPI.ShortUser): void;
  onPressGroup(group: MBAPI.ChannelGroup): void;
}

interface MBChatInputMentionListState {
  selectedIndex: number;
  hovering: boolean;
}

/*
    This mention list is somewhat complex and does multiple things:

    * On mount we start intercepting keystrokes. When the user presses
      ⬆️ or ⬇️ we navigate in the list of mentions . If we press <enter>,
      we call onPress{User|Group}
    * When the selected index updates, we make sure to scroll to that
      index so that the selected item is always visible.
    * When hovering over an index, we don't show the selected index
      to avoid confusion. Only one item should look selected at a time.
    * When updating the content of the list, we reset the selected index
      so that it always starts at the top.
*/
export class MBChatInputMentionList extends React.PureComponent<
  MBChatInputMentionListProps,
  MBChatInputMentionListState
> {
  sectionList = React.createRef<SectionList<any>>();
  state: MBChatInputMentionListState = {
    selectedIndex: 0,
    hovering: false,
  };

  // We keep track of if we're currently hovering over an item.
  // If we are, we don't want to show the currently selected
  // index (controlled by the keyboard arrows).
  onHoverStart = () => this.setState({ hovering: true });
  onHoverEnd = () => this.setState({ hovering: false });

  renderGroupItem = ({
    index,
    item,
  }: {
    index: number;
    item: MBAPI.ShortUser | MBAPI.ChannelGroup;
  }) => {
    const group = item as MBAPI.ChannelGroup;
    return (
      <MBChatInputMentionRowWithOnPress
        key={group.id}
        title={`${group.name}`}
        description={group.description}
        item={group}
        onPress={this.props.onPressGroup}
        image={<MBUserCollage size="small" users={group.users} />}
        selected={!this.state.hovering && this.state.selectedIndex === index}
        onHoverEnd={this.onHoverEnd}
        onHoverStart={this.onHoverStart}
      />
    );
  };

  renderUserItem = ({
    index,
    item,
  }: {
    index: number;
    item: MBAPI.ShortUser | MBAPI.ChannelGroup;
  }) => {
    const user = item as MBAPI.ShortUser;
    // Note(fant): for users, we need to find the currently
    // hovered index in the user list.
    const selectedIndexInSection = this.state.selectedIndex - this.props.groups.length;
    return (
      <MBChatInputMentionRowWithOnPress
        key={user.id}
        title={`@${user.username}`}
        item={user}
        onPress={this.props.onPressUser}
        image={<MBUserImage size="small" user={user} />}
        selected={!this.state.hovering && selectedIndexInSection === index}
        onHoverEnd={this.onHoverEnd}
        onHoverStart={this.onHoverStart}
      />
    );
  };

  handleWebKeyDown = (event: KeyboardEvent) => {
    const listLength = this.props.groups.length + this.props.users.length;

    // In all events below, we prevent the chat input from being affected.
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        // We add listLength so that pressing up when at index 0 goes to the last item
        const prevIndex = (this.state.selectedIndex + listLength - 1) % listLength;
        this.setState({ selectedIndex: prevIndex });
        break;
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = (this.state.selectedIndex + 1) % listLength;
        this.setState({ selectedIndex: nextIndex });
        break;
      case 'Enter':
      case 'NumpadEnter':
        event.preventDefault();
        if (this.state.selectedIndex < this.props.groups.length) {
          const group = this.props.groups[this.state.selectedIndex];
          this.props.onPressGroup(group);
        } else {
          const index = this.state.selectedIndex - this.props.groups.length;
          const user = this.props.users[index];
          this.props.onPressUser(user);
        }
        break;
    }
  };

  componentDidMount() {
    // Why do we use "keydown" and not eg "keypress"?
    // Because arrow events aren't fired for "keypress".
    document.addEventListener('keydown', this.handleWebKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleWebKeyDown);
  }

  UNSAFE_componentWillUpdate(nextProps: MBChatInputMentionListProps) {
    // We reset the selected index every time the list updates.
    // In this way, the selected item (controlled by up/down arrows
    // is always visible at the top when updating the list).
    if (
      nextProps.users.length !== this.props.users.length ||
      nextProps.groups.length !== this.props.groups.length
    ) {
      this.setState({ selectedIndex: 0 });
    }
  }

  componentDidUpdate(_unused: MBChatInputMentionListProps, prevState: MBChatInputMentionListState) {
    // When the selected index changes, we want to make sure that
    // the selected row is currently visible. viewPosition 0.5
    // makes the list scroll the item to the middle of the list.
    if (this.state.selectedIndex !== prevState.selectedIndex && !!this.sectionList.current) {
      if (this.state.selectedIndex < this.props.groups.length) {
        // If we're in the group section, scroll to the group item
        this.sectionList.current.scrollToLocation({
          sectionIndex: 0,
          itemIndex: this.state.selectedIndex,
          viewPosition: 0.5,
        });
      } else {
        // Otherwise, translate the selected index to the index in the user list
        this.sectionList.current.scrollToLocation({
          sectionIndex: 1,
          itemIndex: this.state.selectedIndex - this.props.groups.length,
          viewPosition: 0.5,
        });
      }
    }
  }
  render() {
    // Keyboard prop below are to prevent the keyboard from being dismissed
    // when pressing an item in the list: https://stackoverflow.com/a/45884998/9117995
    // extraData makes the list re-render when the state is updated:
    // https://stackoverflow.com/a/47562302/9117995
    return (
      <SectionList<MBAPI.ShortUser | MBAPI.ChannelGroup>
        ref={this.sectionList as any}
        style={Styles.mentionList}
        keyExtractor={keyExtractor}
        sections={[
          { data: this.props.groups, renderItem: this.renderGroupItem },
          { data: this.props.users, renderItem: this.renderUserItem },
        ]}
        keyboardShouldPersistTaps="always"
        extraData={this.state}
      />
    );
  }
}
